import { ReactNode, useState } from 'react';
import * as R from 'remeda';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@rossum/ui/material';
import { To, useComponentsContext, useIntl } from '../ComponentsContext';
import DialogTitle from '../dialog-title/DialogTitle';

type LeavingDialogProps = {
  when: boolean;
  confirmCallback?: () => void;
  title?: ReactNode;
  description?: ReactNode;
};

const withForceNavigation = (target: To): To => {
  if (typeof target === 'string') {
    return { pathname: target, state: { forceNavigation: true } };
  }
  const existingState = R.isPlainObject(target.state) ? target.state : {};
  return {
    ...target,
    state: { ...existingState, forceNavigation: true },
  };
};

// Although this is not a proper UI component, let's put it to UI right now.
export const LeavingDialog = ({
  when,
  confirmCallback,
  title,
  description,
}: LeavingDialogProps) => {
  const [pendingTarget, setPendingTarget] = useState<To | undefined>(undefined);

  const intl = useIntl();
  const { LinkComponent, PromptComponent } = useComponentsContext();

  return (
    <>
      <Dialog
        data-cy="leaving-dialog"
        open={!!pendingTarget}
        onClose={() => setPendingTarget(undefined)}
        sx={{ transition: 'smooth' }}
        PaperProps={{
          sx: { width: 480, minHeight: 213, position: 'fixed' },
          elevation: 2,
        }}
      >
        {pendingTarget ? (
          <>
            <DialogTitle
              title={
                title ??
                intl.formatMessage({
                  id: 'components.leavingDialog.title',
                })
              }
              onClose={() => setPendingTarget(undefined)}
            />
            <DialogContent sx={{ mx: 4, mt: 4, p: 0 }}>
              {description ??
                intl.formatMessage({
                  id: 'components.leavingDialog.description',
                })}
            </DialogContent>
            <DialogActions sx={{ pb: 4, pt: 2.5, mr: 4, pr: 0 }}>
              {confirmCallback ? (
                <Button
                  variant="outlined"
                  size="medium"
                  color="secondary"
                  data-cy="leaving-dialog-confirm"
                  onClick={confirmCallback}
                >
                  {intl.formatMessage({
                    id: 'components.leavingDialog.discard',
                  })}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="medium"
                  color="secondary"
                  data-cy="leaving-dialog-confirm"
                  component={LinkComponent}
                  to={withForceNavigation(pendingTarget)}
                >
                  {intl.formatMessage({
                    id: 'components.leavingDialog.discard',
                  })}
                </Button>
              )}
              <Button
                variant="contained"
                size="medium"
                color="primary"
                data-cy="leaving-dialog-cancel"
                onClick={() => setPendingTarget(undefined)}
              >
                {intl.formatMessage({
                  id: 'components.leavingDialog.continueEditing',
                })}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
      {PromptComponent ? (
        <PromptComponent
          when={when}
          message={nextLocation => {
            const state =
              typeof nextLocation === 'string' ? undefined : nextLocation.state;

            if (
              R.isPlainObject(state) &&
              'forceNavigation' in state &&
              !!state['forceNavigation']
            ) {
              return true;
            }

            setPendingTarget(prev => (prev ? undefined : nextLocation));
            return pendingTarget !== undefined;
          }}
        />
      ) : null}
    </>
  );
};
